import uniqueId from 'lodash/uniqueId';
import { useCallback, useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { LocationModel } from 'src/models/locations/location.model';
import { Icon } from 'src/ui/shared/components/icon/Icon';
import { LocationInnerFormData, PeliasFormDataWithId } from './location-form.models';
import { LocationFormFields } from './LocationMultiControlParts/LocationFormFields';
import { usePeliusAdaptors } from './pelias-adaptor.hook';

interface Props {
    onChange: (locations: LocationModel[]) => void;
    defaultValues: LocationModel[] | undefined;
    defaultCountryCode: string;
}

export const LocationMultiControl = ({
    onChange,
    defaultCountryCode,
    defaultValues,
}: Props): React.ReactElement => {
    const {
        formState: { isValidating: isParentFormValidating },
    } = useFormContext();
    const { peliasToLocationAdaptor, multiLocationToPeliasAdaptor } = usePeliusAdaptors();

    const createEmpty = useCallback(() => {
        const unique = uniqueId();
        const empty: PeliasFormDataWithId = {
            id: unique,
            countryCode: defaultCountryCode,
            city: null,
            postcode: null,
            region: null,
            street: null,
        };
        return { id: unique, item: empty };
    }, [defaultCountryCode]);

    const innerDefaultValues = useMemo(() => {
        if (!defaultValues || defaultValues.length === 0) {
            const { id, item } = createEmpty();
            return { [id]: item };
        }
        return multiLocationToPeliasAdaptor(defaultValues);
    }, [createEmpty, defaultValues, multiLocationToPeliasAdaptor]);

    const formMethods = useForm<LocationInnerFormData>({
        mode: 'onChange',
        defaultValues: innerDefaultValues,
    });

    const value = formMethods.watch();

    const ids = useMemo(() => {
        return Object.keys(value);
    }, [value]);

    const onAdd = useCallback(() => {
        const { id, item } = createEmpty();
        formMethods.setValue(id, item);
    }, [createEmpty, formMethods]);

    useEffect(() => {
        const toSend = ids
            .map(id => value[id])
            .filter(x => !!x)
            .map(innerField => peliasToLocationAdaptor(innerField));

        onChange(toSend);
    }, [peliasToLocationAdaptor, onChange, value, ids, formMethods]);

    useEffect(() => {
        if (isParentFormValidating) {
            void formMethods.trigger();
        }
    }, [formMethods, isParentFormValidating]);

    return (
        <FormProvider {...formMethods}>
            <div className="d-grid grid-1 gap-3">
                {ids.map(id => (
                    <LocationFormFields key={id} id={id} />
                ))}
                <div>
                    <Button variant="outline-input" onClick={onAdd}>
                        <span className="text-dark">
                            <Icon icon="plus-lg">Add new location</Icon>
                        </span>
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
};
